/** Shadows/Highlights adjustment — pure math + types, no React,
 *  no Fabric.
 *
 *  Per-pixel luminance-band recovery. Two regions (shadows /
 *  highlights), each with two parameters:
 *
 *  - `*Amount` (-100..+100): signed shift for that region.
 *    Positive lifts (toward white), negative crushes (toward black).
 *    Photoshop's S/H dialog only exposes positive (recovery), but a
 *    signed range gives users contrast-pushing in the same panel.
 *  - `*TonalWidth` (0..100): how broad the band is.
 *    0 = only the very darkest/brightest pixels affected;
 *    100 = the band extends well into midtones.
 *    No spatial blur — that's "Radius" in Photoshop and is
 *    deferred (would require an offscreen blurred-luminance map).
 *
 *  Region masks are power curves of luminance:
 *    shadowsW = (1 − L)^k     where k narrows the band as it grows
 *    highlightsW = L^k        same shape mirrored
 *  k is derived from TonalWidth: width=100 → k=1 (broad), width=0
 *  → k=5 (narrow), width=50 → k=3 (default).
 *
 *  Per-channel application sums the signed shadow + highlight
 *  shifts and blends toward 1 (positive) or 0 (negative). When the
 *  two regions don't overlap (shadowsW × highlightsW ~ 0 in their
 *  active zones) the math is well-behaved; midtones see only the
 *  faint tails of both curves. */

export interface ShadowsHighlightsValue {
  shadowsAmount: number;
  shadowsTonalWidth: number;
  highlightsAmount: number;
  highlightsTonalWidth: number;
}

export const identityShadowsHighlightsValue = (): ShadowsHighlightsValue => ({
  shadowsAmount: 0,
  shadowsTonalWidth: 50,
  highlightsAmount: 0,
  highlightsTonalWidth: 50,
});

const LUMA_R = 0.2126;
const LUMA_G = 0.7152;
const LUMA_B = 0.0722;

/** Pure pixel transform — returns a fresh ImageData. Tests use
 *  this variant; the canvas-bound wrapper does the putImageData. */
export function applyShadowsHighlightsPixels(
  source: ImageData,
  value: ShadowsHighlightsValue,
): ImageData {
  const w = source.width;
  const h = source.height;
  const sd = source.data;
  const out = new Uint8ClampedArray(sd.length);

  // Derive band-shape exponents. width=100 → k=1 (broadest),
  // width=0 → k=5 (narrowest), width=50 → k=3 (default).
  const sK = 1 + (1 - clamp01(value.shadowsTonalWidth / 100)) * 4;
  const hK = 1 + (1 - clamp01(value.highlightsTonalWidth / 100)) * 4;
  const sAmt = value.shadowsAmount / 100;
  const hAmt = value.highlightsAmount / 100;

  for (let i = 0; i < sd.length; i += 4) {
    const r0 = sd[i] / 255;
    const g0 = sd[i + 1] / 255;
    const b0 = sd[i + 2] / 255;

    const L = LUMA_R * r0 + LUMA_G * g0 + LUMA_B * b0;
    const sW = Math.pow(1 - L, sK);
    const hW = Math.pow(L, hK);

    const shift = sAmt * sW + hAmt * hW;

    let nr = r0;
    let ng = g0;
    let nb = b0;
    if (shift > 0) {
      nr = r0 + (1 - r0) * shift;
      ng = g0 + (1 - g0) * shift;
      nb = b0 + (1 - b0) * shift;
    } else if (shift < 0) {
      nr = r0 + r0 * shift;
      ng = g0 + g0 * shift;
      nb = b0 + b0 * shift;
    }

    if (nr < 0) nr = 0;
    else if (nr > 1) nr = 1;
    if (ng < 0) ng = 0;
    else if (ng > 1) ng = 1;
    if (nb < 0) nb = 0;
    else if (nb > 1) nb = 1;

    out[i] = (nr * 255 + 0.5) | 0;
    out[i + 1] = (ng * 255 + 0.5) | 0;
    out[i + 2] = (nb * 255 + 0.5) | 0;
    out[i + 3] = sd[i + 3];
  }
  return new ImageData(out, w, h);
}

export function applyShadowsHighlightsToCanvas(
  source: ImageData,
  target: HTMLCanvasElement,
  value: ShadowsHighlightsValue,
): void {
  const tCtx = target.getContext('2d');
  if (!tCtx) return;
  if (target.width !== source.width || target.height !== source.height) {
    target.width = source.width;
    target.height = source.height;
  }
  tCtx.putImageData(applyShadowsHighlightsPixels(source, value), 0, 0);
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
